'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { Suspense } from 'react';

import { Anchor, Group, Stack } from '@mantine/core';

import { BlogTeaser } from '@marketing/components/blog-teaser/BlogTeaser';
import { Section } from '@marketing/components/section/Section';
import { SectionHeader } from '@marketing/components/section/SectionHeader';
import { TeaserGrid } from '@marketing/components/teaser-grid/TeaserGrid';
import { BlogCategory, blogCategories } from '@marketing/data/blog-categories';
import { blogPosts } from '@marketing/data/blog-posts';

const byDate = (a: { date: string }, b: { date: string }) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
};

const Blog = () => {
    const searchParams = useSearchParams();
    const activeCategory = (searchParams?.get('category') || null) as BlogCategory | null;

    const filteredPosts = (
        activeCategory
            ? blogPosts.filter((post) => post.category.toLowerCase() === activeCategory.toLowerCase())
            : blogPosts
    )
        .slice()
        .sort(byDate);

    return (
        <>
            <Section data-with-bg="grid">
                <Stack gap="xl" align="center">
                    <SectionHeader
                        prefix="Insights & Updates"
                        title={
                            <>
                                The DCIDE <span className="gradient-cyan-green">Blog</span>
                            </>
                        }
                        description="Stay up to date with the latest features, tutorials, and news from the DCIDE team."
                    />

                    <Group gap="xl" justify="center" mt="xl" mb={-80}>
                        <Anchor
                            component={Link}
                            href="/blog"
                            fz="lg"
                            fw={activeCategory === null ? 700 : 500}
                            opacity={activeCategory === null ? 1 : 0.5}
                            style={{
                                textDecorationColor: activeCategory === null ? 'currentColor' : 'transparent',
                            }}
                        >
                            All
                        </Anchor>
                        {Object.values(blogCategories).map((category) => (
                            <Anchor
                                key={category.key}
                                component={Link}
                                href={`/blog?category=${category.key}`}
                                fz="lg"
                                fw={activeCategory === category.key ? 700 : 500}
                                opacity={activeCategory === category.key ? 1 : 0.5}
                                style={{
                                    textDecorationColor:
                                        activeCategory === category.key ? 'currentColor' : 'transparent',
                                }}
                            >
                                {category.label}
                            </Anchor>
                        ))}
                    </Group>
                </Stack>
            </Section>

            <Section variant="light" bg="white">
                <TeaserGrid>
                    {filteredPosts.map((post) => (
                        <BlogTeaser blog={post} key={post.slug ?? post.href} />
                    ))}
                </TeaserGrid>
            </Section>
        </>
    );
};

const BlogWithSuspense = () => (
    <Suspense fallback={<></>}>
        <Blog />
    </Suspense>
);

export default BlogWithSuspense;
